import { Link } from "react-router-dom";
import KtrlLayout from "../../components/ktrl/KtrlLayout";

const Tag = ({ children }) => (
  <span className="inline-block text-xs font-semibold uppercase tracking-widest text-indigo-600 mb-3">
    {children}
  </span>
);

const services = [
  {
    icon: "💬",
    title: "Kundeservice",
    text: "Verktøy som håndterer henvendelser, FAQ og eskalering — med mennesker i loopen for alt som krever vurdering.",
  },
  {
    icon: "📈",
    title: "Digital markedsføring",
    text: "AI-assistert innholdsproduksjon, SEO/SEM-optimalisering og rapportering, med kvalitetssikring fra teamet deres.",
  },
  {
    icon: "📋",
    title: "Administrativ støtte",
    text: "Skreddersydde verktøy for e-posthåndtering, kalender, koordinering og dokumenthåndtering — bygget rundt hvordan dere faktisk jobber.",
  },
  {
    icon: "🎯",
    title: "Salgsstøtte",
    text: "Systemer for lead-generering, prospecting, CRM-vedlikehold og automatisk oppfølging, tilpasset deres salgsprosess.",
  },
];

const stats = [
  { value: "2 uker", label: "Fra idé til fungerende prototype" },
  { value: "48t", label: "Fra bestilling til produksjon" },
  { value: "24/7", label: "Alltid tilgjengelig" },
  { value: "100%", label: "Tilfredshetsgaranti" },
];

const trustCards = [
  {
    icon: "🛡️",
    title: "Menneskelig backup inkludert",
    text: "Det er alltid et menneske bak. For edge cases, komplekse avgjørelser og kvalitetssikring har vi dedikerte folk som overvåker og griper inn når det trengs.",
  },
  {
    icon: "🤖",
    title: "Ikke enda en frustrerende chatbot",
    text: "Vi har alle møtt en chatbot som ikke forstår spørsmålet, eller som ikke har fullmakt til å hjelpe. Verktøyene vi bygger er laget for å faktisk løse oppgaven — og sier tydelig ifra når et menneske må kobles på.",
  },
  {
    icon: "🧠",
    title: "Intelligent output — ikke AI slop",
    text: "AI slop oppstår når kunstig intelligens brukes uten kontekst og uten mennesker i loopen. Løsningene våre gir aldri generiske svar — de opererer utelukkende fra bedriftens egen kunnskapsbase og rutiner, med menneskelig godkjenning innebygd som standard og full sporbarhet på hvorfor de svarte som de gjorde.",
  },
];

const securityCards = [
  {
    icon: "🇪🇺",
    title: "GDPR-vennlig hosting",
    text: "Løsningene vi bygger bor på servere hos Hetzner i Tyskland eller Finland. Dine kundedata forlater aldri EØS, og alle tilganger er strengt kontrollert.",
  },
  {
    icon: "🔍",
    title: "Sporbarhetslogger",
    text: "All kommunikasjon og resonnering systemet gjør oppbevares, slik at du kan se hvorfor det gjorde som det gjorde.",
  },
  {
    icon: "⚙️",
    title: "Valgfri språkmodell",
    text: "Vi bruker et bredt utvalg av språkmodeller, fra de kraftigste frontier-modellene som Claude Opus 4.6 til enklere og billigere åpen-vekt modeller. Dere velger basert på krav til pris og ytelse, og kan bytte underveis.",
  },
  {
    icon: "🧠",
    title: "Kunnskapsgrunnlag fra din bedrift",
    text: "Løsningene vi bygger gir ikke generelle svar — de bruker en skreddersydd kunnskapsbase som vi lager basert på deres egen informasjon.",
  },
];

const portfolio = [
  { name: "aimoko", url: "https://aimoko.no/", text: "AI-native productivity OS. Arbeidsverktøy for neste generasjon." },
  { name: "Velocity North", url: "https://velocitynorth.ai/", text: "Performance marketing agency med AI-drevet kampanjestyring og rapportering." },
  { name: "Klaw", url: "https://klaw.ktrl.no/", text: "Managed AI assistant hosting. Skalerbare AI-agenter for bedrifter." },
  { name: "Ketchup", url: "https://ketchup.ktrl.no/", text: "Lese-app for barn med høyt skolefravær." },
  { name: "Klinikk for sunn aldring", url: "https://klinikkforsunnaldring.no/", text: "Helseoptimalisering og forebygging — medisinsk klinikk med fokus på sunn aldring og livsstilsmedisin." },
  { name: "Digtective", url: "https://digtective.com/", text: "Conversion tracking — forstå hvilke kanaler som faktisk driver resultater." },
];

const KtrlHomePage = () => {
  return (
    <KtrlLayout>
      {/* Hero / Om KTRL */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <Tag>Om KTRL</Tag>
        <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6 max-w-3xl">
          Hva vi gjør
        </h1>
        <p className="text-lg text-slate-600 leading-relaxed max-w-2xl">
          KTRL AS opererer i krysningen mellom finans, drift og teknologi. Vi bygger programvare og
          AI-drevne verktøy for bedrifter som vil henge med — fra interne systemer til kundevendte
          løsninger. Vår kjernekompetanse er å omsette komplekse tekniske muligheter til konkret
          forretningsverdi — raskt, målbart og skalerbart.
        </p>
      </section>

      {/* Tjenester */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Tag>Tjenester</Tag>
          <h2 className="text-2xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6 max-w-2xl">
            Vi bygger det dere trenger for å lykkes med AI
          </h2>
          <p className="text-slate-600 leading-relaxed max-w-2xl mb-12">
            Mange bedrifter vet de bør ta i bruk AI, men mangler tid, folk eller teknisk kompetanse til
            å gjøre det riktig. Vi bygger skreddersydde apper, integrasjoner og AI-verktøy som løser
            konkrete problemer i deres virksomhet — fra automatisering av rutineoppgaver til
            fullverdige produkter.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-16">
            {services.map((s) => (
              <div key={s.title} className="bg-white border border-slate-200 rounded-xl p-6">
                <div className="text-2xl mb-3">{s.icon}</div>
                <h3 className="font-semibold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
            {stats.map((s) => (
              <div key={s.label}>
                <div className="text-3xl font-bold text-indigo-600">{s.value}</div>
                <div className="text-sm text-slate-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {trustCards.map((c) => (
              <div key={c.title} className="bg-white border border-slate-200 rounded-xl p-6">
                <div className="text-2xl mb-3">{c.icon}</div>
                <h4 className="font-semibold text-slate-900 mb-2">{c.title}</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sikkerhet */}
      <section className="border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Tag>Sikkerhet</Tag>
          <h2 className="text-2xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6 max-w-2xl">
            Sikkerhet og sporbarhet i høysetet
          </h2>
          <p className="text-slate-600 leading-relaxed max-w-2xl mb-12">
            Kunstig intelligens blir ikke produktivt hvis ikke det er sikkert og sporbart.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {securityCards.map((c) => (
              <div key={c.title} className="border border-slate-200 rounded-xl p-6">
                <div className="text-2xl mb-3">{c.icon}</div>
                <h4 className="font-semibold text-slate-900 mb-2">{c.title}</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portefølje */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Tag>Portefølje</Tag>
          <h2 className="text-2xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6 max-w-2xl">
            Selskapene vi bygger
          </h2>
          <p className="text-slate-600 leading-relaxed max-w-2xl mb-12">
            KTRL investerer i og drifter en portefølje av teknologiselskaper innen AI, markedsføring og
            digital helse.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {portfolio.map((p) => (
              <a
                key={p.name}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white border border-slate-200 rounded-xl p-6 hover:border-indigo-400 hover:shadow-md transition-all"
              >
                <h3 className="font-semibold text-slate-900 mb-2">{p.name}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-3">{p.text}</p>
                <span className="text-sm font-medium text-indigo-600 group-hover:underline">
                  {p.url.replace("https://", "")} →
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Historien bak navnet */}
      <section className="border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Tag>Historien bak navnet</Tag>
          <h2 className="text-2xl md:text-4xl font-bold tracking-tight text-slate-900 mb-8">
            K T R L
          </h2>
          <div className="max-w-2xl">
            <p className="text-slate-600 leading-relaxed mb-4">
              Navnet KTRL stammer fra et prosjekt som aldri ble fullført. En nær venn og
              forretningspartner hadde selv opplevd de bunnløse gjeldsproblemene som rammer mange
              nordmenn — og vi planla å bygge en tjeneste som skulle hjelpe folk å få{" "}
              <strong>KonTRoLl</strong> over sin egen økonomi, ved hjelp av en betrodd venn. Han ble
              borte før vi rakk å fullføre det.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Da jeg trengte et holdingselskap, var KTRL det naturlige valget — som et levende minne.
              Og det viste seg å passe utmerket til alt vi gjør: gi bedrifter kontroll på
              kundeservice, markedsføring, administrasjon, e-post og produktivitet. Kontroll — levert
              av intelligente systemer.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-slate-200 bg-indigo-600">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <p className="text-indigo-200 text-sm font-semibold uppercase tracking-widest mb-2">
              Klar for neste steg?
            </p>
            <h3 className="text-2xl font-bold text-white">Ta kontakt</h3>
          </div>
          <Link
            to="/tjenester"
            className="inline-block bg-white text-indigo-600 font-semibold px-6 py-3 rounded-md hover:bg-indigo-50 transition-colors"
          >
            Se våre tjenester
          </Link>
        </div>
      </section>
    </KtrlLayout>
  );
};

export default KtrlHomePage;
